import { LogOut, Menu, UserRound } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { AppNav } from "@/components/layout/app-nav";
import { LanguageSwitcher } from "@/components/layout/language-switcher";
import { ThemeToggle } from "@/components/layout/theme-toggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { WorkspaceSwitcher } from "@/components/layout/workspace-switcher";
import { useAuth } from "@/lib/session/auth-context";
import { initials } from "@/lib/utils/format";
import { useWorkspace } from "@/lib/session/workspace-context";

export function SiteHeader() {
  const { t } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // The installed app draws under the status bar (viewport-fit=cover, black-translucent).
  return (
    <header className="sticky top-0 z-40 border-b bg-background/90 pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="flex h-14 items-center gap-2 px-4">
        {isAuthenticated && workspace && (
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" aria-label={t("nav.main")}>
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-4 pt-[calc(1rem+env(safe-area-inset-top))]">
              <SheetTitle className="sr-only">{t("nav.main")}</SheetTitle>
              <div className="mt-8">
                <AppNav onNavigate={() => setMenuOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        )}

        <Link to="/" className="flex items-center gap-2 font-semibold" data-testid="brand">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="hidden font-display text-xl font-semibold tracking-tight sm:inline">
            PrestoBooks
          </span>
        </Link>

        {isAuthenticated && (
          <>
            <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
            <WorkspaceSwitcher />
          </>
        )}

        <div className="ml-auto flex items-center gap-1">
          {/* Signed in, the language is a profile setting; on the login page it has to be here. */}
          {!isAuthenticated && <LanguageSwitcher />}
          <ThemeToggle />
          {isAuthenticated && user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full"
                  data-testid="user-menu"
                >
                  <Avatar className="size-8">
                    <AvatarFallback>{initials(user.name || user.email)}</AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel data-testid="user-name">
                  {user.name || user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate("/profile")}>
                  <UserRound /> {t("nav.profile")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout"
                  onClick={() => {
                    // Leave the protected page first so its guard does not redirect to /login.
                    navigate("/", { replace: true });
                    logout();
                  }}
                >
                  <LogOut /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button asChild size="sm" data-testid="login-link">
              <Link to="/login">{t("nav.login")}</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
